import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, TextField, Button,
    Paper, Grid, Chip, Divider, Alert, CircularProgress, Tabs, Tab
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SearchIcon from '@mui/icons-material/Search';

// Corregir icono por defecto de Leaflet en webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Componente auxiliar para mover el mapa cuando cambian las coordenadas
const MoverMapa = ({ lat, lon }) => {
    const map = useMap();
    useEffect(() => {
        map.setView([lat, lon], map.getZoom());
    }, [lat, lon, map]);
    return null;
};

const MapaSeguimiento = () => {
    const [tab, setTab] = useState(0);
    const [latInput, setLatInput] = useState('4.7110');
    const [lonInput, setLonInput] = useState('-74.0721');
    const [etiqueta, setEtiqueta] = useState('Mi ubicación');
    const [direccion, setDireccion] = useState('');
    const [buscandoDireccion, setBuscandoDireccion] = useState(false);
    const [resultadosDireccion, setResultadosDireccion] = useState([]);
    const [errorDireccion, setErrorDireccion] = useState('');
    const [puntos, setPuntos] = useState([
        { lat: 4.7110, lon: -74.0721, etiqueta: 'Bogotá (por defecto)', ts: new Date().toLocaleTimeString('es-CO') }
    ]);
    const [error, setError] = useState('');
    const [centro, setCentro] = useState({ lat: 4.7110, lon: -74.0721 });

    const buscarDireccion = async () => {
        if (!direccion.trim()) return;
        setBuscandoDireccion(true);
        setErrorDireccion('');
        setResultadosDireccion([]);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(direccion)}&format=json&limit=5&addressdetails=1`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await res.json();
            if (data.length === 0) {
                setErrorDireccion('No se encontró la dirección. Intenta ser más específico (ej: "Calle 100 #15-20, Bogotá, Colombia").');
            } else {
                setResultadosDireccion(data);
            }
        } catch {
            setErrorDireccion('Error al conectar con el servicio de geocodificación.');
        } finally {
            setBuscandoDireccion(false);
        }
    };

    const seleccionarResultado = (r) => {
        const lat = parseFloat(r.lat);
        const lon = parseFloat(r.lon);
        const nombre = r.display_name.split(',').slice(0, 3).join(',');
        setLatInput(lat.toFixed(6));
        setLonInput(lon.toFixed(6));
        setEtiqueta(nombre);
        setResultadosDireccion([]);
        setDireccion(r.display_name.split(',').slice(0, 2).join(','));
        const nuevo = { lat, lon, etiqueta: nombre, ts: new Date().toLocaleTimeString('es-CO') };
        setPuntos(prev => [...prev, nuevo]);
        setCentro({ lat, lon });
    };

    const validar = (val) => {
        const n = parseFloat(val);
        return !isNaN(n) && isFinite(n);
    };

    const agregarPunto = () => {
        setError('');
        const lat = parseFloat(latInput);
        const lon = parseFloat(lonInput);

        if (!validar(latInput) || lat < -90 || lat > 90) {
            setError('Latitud inválida. Debe estar entre -90 y 90.');
            return;
        }
        if (!validar(lonInput) || lon < -180 || lon > 180) {
            setError('Longitud inválida. Debe estar entre -180 y 180.');
            return;
        }

        const nuevo = { lat, lon, etiqueta: etiqueta.trim() || `Punto ${puntos.length + 1}`, ts: new Date().toLocaleTimeString('es-CO') };
        setPuntos(prev => [...prev, nuevo]);
        setCentro({ lat, lon });
    };

    const limpiarPuntos = () => {
        setPuntos([]);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                📍 Mapa de Seguimiento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ingresa coordenadas para situar puntos en el mapa. Compatible con GPS.
            </Typography>

            <Grid container spacing={3}>
                {/* Panel de entrada */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Ingresar ubicación
                        </Typography>
                        <Divider sx={{ mb: 1 }} />

                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="fullWidth">
                            <Tab label="Por dirección" sx={{ fontSize: 12, minHeight: 36 }} />
                            <Tab label="Por coordenadas" sx={{ fontSize: 12, minHeight: 36 }} />
                        </Tabs>

                        {/* TAB 0: búsqueda por dirección */}
                        {tab === 0 && (
                            <Box>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Dirección"
                                    value={direccion}
                                    onChange={e => { setDireccion(e.target.value); setResultadosDireccion([]); setErrorDireccion(''); }}
                                    onKeyDown={e => e.key === 'Enter' && buscarDireccion()}
                                    placeholder="Ej: Calle 100 #15-20, Bogotá"
                                    sx={{ mb: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={buscandoDireccion ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                                    onClick={buscarDireccion}
                                    disabled={buscandoDireccion || !direccion.trim()}
                                    sx={{ mb: 1 }}
                                >
                                    {buscandoDireccion ? 'Buscando...' : 'Buscar dirección'}
                                </Button>

                                {errorDireccion && <Alert severity="warning" sx={{ mb: 1, fontSize: 12 }}>{errorDireccion}</Alert>}

                                {resultadosDireccion.length > 0 && (
                                    <Box sx={{ border: '1px solid #444', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                                        <Typography variant="caption" sx={{ px: 1, py: 0.5, display: 'block', bgcolor: '#333', color: '#aaa' }}>
                                            Selecciona un resultado:
                                        </Typography>
                                        {resultadosDireccion.map((r, i) => (
                                            <Box
                                                key={i}
                                                onClick={() => seleccionarResultado(r)}
                                                sx={{
                                                    p: 1, cursor: 'pointer',
                                                    borderBottom: i < resultadosDireccion.length - 1 ? '1px solid #333' : 'none',
                                                    bgcolor: '#1e1e1e', '&:hover': { bgcolor: '#0d2a4a' }
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: '#fff', fontSize: 12 }}>
                                                    📍 {r.display_name.split(',').slice(0, 3).join(',')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#90caf9', fontFamily: 'monospace' }}>
                                                    {parseFloat(r.lat).toFixed(5)}, {parseFloat(r.lon).toFixed(5)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* TAB 1: coordenadas manuales */}
                        {tab === 1 && (
                        <Box>
                        <TextField
                            fullWidth
                            size="small"
                            label="Latitud"
                            value={latInput}
                            onChange={e => setLatInput(e.target.value)}
                            placeholder="Ej: 6.2442"
                            helperText="Entre -90 y 90"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Longitud"
                            value={lonInput}
                            onChange={e => setLonInput(e.target.value)}
                            placeholder="Ej: -75.5812"
                            helperText="Entre -180 y 180"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Etiqueta del punto"
                            value={etiqueta}
                            onChange={e => setEtiqueta(e.target.value)}
                            placeholder="Nombre del punto"
                            sx={{ mb: 2 }}
                        />

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={agregarPunto}
                            >
                                Situar en mapa
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={limpiarPuntos}
                                disabled={puntos.length === 0}
                            >
                                Limpiar
                            </Button>
                        </Box>
                        </Box>
                        )}

                        {/* Lista de puntos agregados */}
                        {puntos.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    Puntos registrados ({puntos.length})
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                                    {[...puntos].reverse().map((p, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                mb: 1, p: 1, borderRadius: 1,
                                                bgcolor: i === 0 ? '#0d2a4a' : '#1e1e1e',
                                                border: i === 0 ? '1px solid #42a5f5' : '1px solid #444',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setCentro({ lat: p.lat, lon: p.lon })}
                                        >
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff' }}>
                                                📍 {p.etiqueta}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#90caf9' }}>
                                                {p.lat.toFixed(6)}, {p.lon.toFixed(6)}
                                            </Typography>
                                            <Box>
                                                <Chip label={p.ts} size="small" sx={{ mt: 0.5, fontSize: 10, height: 18, bgcolor: '#333', color: '#fff' }} />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Mapa */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                        <MapContainer
                            center={[centro.lat, centro.lon]}
                            zoom={13}
                            style={{ height: '550px', width: '100%' }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MoverMapa lat={centro.lat} lon={centro.lon} />
                            {puntos.map((p, i) => (
                                <Marker key={i} position={[p.lat, p.lon]}>
                                    <Popup>
                                        <strong>{p.etiqueta}</strong><br />
                                        Lat: {p.lat.toFixed(6)}<br />
                                        Lon: {p.lon.toFixed(6)}<br />
                                        <small>{p.ts}</small>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default MapaSeguimiento;
